
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { DateTime } from 'luxon';
import { Debounce } from '@/utils/decorators';
import Toast from '@/utils/toast';
import TextButton from '@/components/shared/suites/TextButton.vue';

const namespace: string = 'dashboard';

@Component({
	components: {
		TextButton
	}
})
export default class DashboardQuotes extends Vue {
	@Action('setQuotesOffset', { namespace }) private setQuotesOffset!: (offset: number) => Promise<void>;
	@Getter('getQuotes', { namespace }) private quotes!: DashboardQuoteOrder[];
	@Getter('getQuotesCount', { namespace }) private quotesCount!: number;
	@Getter('getTimezone', { namespace: 'auth' }) private timezone!: string;

	$refs!: {
		quotesList: HTMLElement;
		quotesLoading: HTMLElement;
	};

	private quotesFetching: boolean = false;
	private errorFetchingMoreQuotes: boolean = false;
	private readonly quoteStatusLabels: Record<string, string> = {
		'pending': 'orders.management.pending',
		'in-progress': 'orders.management.in_progress',
		'completed': 'orders.management.completed',
		'cancelled': 'orders.dashboard.quotes.status.declined',
		'expired': 'orders.dashboard.quotes.status.expired'
	};

	private get moreQuotesToLoad(): boolean {
		return this.quotesCount > this.quotes.length;
	}

	/**
	 * Check if more quotes should be fetched on scroll
	 *
	 * @return {boolean}
	 */
	private get loadMoreQuotesOnScroll(): boolean {
		return this.moreQuotesToLoad && !this.quotesFetching && !this.errorFetchingMoreQuotes;
	}

	/**
	 * Set up scroll listener for infinite scroll pagination
	 *
	 * @return {void}
	 */
	private mounted(): void {
		this.$refs.quotesList?.addEventListener('scroll', this.handleListScroll);
	}

	/**
	 * Cleanup scroll listener when component is destroyed
	 *
	 * @return {void}
	 */
	private beforeDestroy(): void {
		this.$refs.quotesList?.removeEventListener('scroll', this.handleListScroll);
	}


	/**
	 * Return the localized label for a quote's display status, treating pending quotes past expiry as expired
	 *
	 * @param {DashboardQuoteOrder} quote
	 * @return {string}
	 */
	private getQuoteStatusLabel(quote: DashboardQuoteOrder): string {
		let status = quote.status;
		if (status === 'pending' && quote.quoteExpiresAt && this.isQuoteExpired(quote.quoteExpiresAt)) {
			status = 'expired';
		}
		return this.$t(this.quoteStatusLabels[status] || 'orders.management.pending');
	}

	/**
	 * Check if a quote's expiry date has passed
	 * Quotes are valid through the end of their expiry date in the location's timezone
	 *
	 * @param {string} quoteExpiresAt
	 * @return {boolean}
	 */
	private isQuoteExpired(quoteExpiresAt: string): boolean {
		return DateTime.fromISO(quoteExpiresAt, { zone: this.timezone }).endOf('day') < DateTime.now();
	}

	/**
	 * Return the localized label for the time left before a quote expires, in days or in hours when it expires today
	 *
	 * @param {string} quoteExpiresAt
	 * @return {string}
	 */
	private getExpiryLabel(quoteExpiresAt: string): string {
		const now = DateTime.now().setZone(this.timezone);
		const expiryDate = DateTime.fromISO(quoteExpiresAt, { zone: this.timezone });
		const daysLeft = expiryDate.startOf('day').diff(now.startOf('day'), 'days').days;
		if (daysLeft > 0) {
			return this.$t('orders.dashboard.quotes.expires_in', { count: daysLeft });
		}
		const hoursLeft = Math.ceil(expiryDate.endOf('day').diff(now, 'hours').hours);
		return this.$t('orders.dashboard.quotes.expires_in_hours', { count: hoursLeft });
	}

	@Debounce(200)
	private handleListScroll(): void {
		this.fetchMoreQuotesOnScroll();
	}

	/**
	 * Fetch more quotes when the user scrolls close to the bottom of the list
	 *
	 * @return {Promise<void>}
	 */
	private async fetchMoreQuotesOnScroll(): Promise<void> {
		const list = this.$refs.quotesList;
		if (this.loadMoreQuotesOnScroll && list && list.scrollTop + list.clientHeight >= list.scrollHeight - 50) {
			this.quotesFetching = true;
			try {
				await this.setQuotesOffset(this.quotes.length);
			}
			catch (errorMessage) {
				this.errorFetchingMoreQuotes = true;
				Toast.error(this.$t('orders.dashboard.quotes.error_fetching', { errorMessage }));
			}
			finally {
				this.quotesFetching = false;
			}
		}
	}

	/**
	 * Clear the fetching error and attempt to fetch again
	 *
	 * @return {void}
	 */
	private clearErrorAndRefetch(): void {
		this.errorFetchingMoreQuotes = false;
		this.fetchMoreQuotesOnScroll();
	}
}
